import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { SavedReport } from "@/entities/savedReports/types";
import { buildScheduleSummary } from "@/components/global/Chat/components/ScheduleModal/buildScheduleSummary";
import { ReportIcon } from "@/components/global/Chat/components/dreamerReports/reportTableCells";
import { ReportActionsMenu } from "@/components/global/Chat/components/dreamerReports/ReportActionsMenu";
import { ReportNameInput } from "@/components/global/Chat/components/dreamerReports/ReportNameInput";
import { useReportRowActions } from "@/components/global/Chat/components/dreamerReports/useReportRowActions";
import { useRenameClickGuard } from "@/components/global/Chat/components/dreamerReports/useRenameClickGuard";
import { useReportRename } from "@/components/global/Chat/components/dreamerReports/useReportRename";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

interface ReportRowProps {
  report: SavedReport;
  onOpen: (report: SavedReport) => void;
  onSchedule: (report: SavedReport) => void;
  onRemoveSchedule: (report: SavedReport) => void;
  onDelete: (report: SavedReport) => void;
}

export const ReportRow: React.FC<ReportRowProps> = ({
  report,
  onOpen,
  onSchedule,
  onRemoveSchedule,
  onDelete
}) => {
  const { t } = useTranslation();
  const [isRenaming, setIsRenaming] = useState(false);

  const handleOpen = useCallback(() => onOpen(report), [onOpen, report]);
  const handleSchedule = useCallback(
    () => onSchedule(report),
    [onSchedule, report]
  );
  const handleRemoveSchedule = useCallback(
    () => onRemoveSchedule(report),
    [onRemoveSchedule, report]
  );
  const handleDelete = useCallback(() => onDelete(report), [onDelete, report]);
  const handleStartRename = useCallback(() => setIsRenaming(true), []);
  const handleFinishRename = useCallback(() => setIsRenaming(false), []);

  const { actions } = useReportRowActions({
    report,
    onOpen: handleOpen,
    onStartRename: handleStartRename,
    onSchedule: handleSchedule,
    onRemoveSchedule: handleRemoveSchedule,
    onDelete: handleDelete
  });

  const { draftName, setDraftName, inputRef, commitRename, cancelRename } =
    useReportRename({
      report,
      isRenaming,
      onFinish: handleFinishRename
    });

  const { onPointerDownCapture, onClickCapture, consumeRenameClick } =
    useRenameClickGuard(isRenaming);
  const handleRowClick = useCallback(
    (event: React.MouseEvent) => {
      if (isRenaming || consumeRenameClick(event)) return;
      handleOpen();
    },
    [isRenaming, consumeRenameClick, handleOpen]
  );

  const scheduleSummary =
    report.isScheduled && report.scheduleConfig
      ? buildScheduleSummary(t, report.scheduleConfig, "recurring")
      : null;

  return (
    // The whole row opens the report. The name stays a button so the row can
    // be reached and opened from the keyboard; its click bubbles up to here.
    <div
      className={cn(
        "group flex items-center gap-3 px-4 py-2 hover:bg-surface-elevation2",
        !isRenaming && "cursor-pointer"
      )}
      onPointerDownCapture={onPointerDownCapture}
      onClickCapture={onClickCapture}
      onClick={handleRowClick}
      data-test="report-row"
    >
      <div className="shrink-0">
        <ReportIcon isScheduled={report.isScheduled} />
      </div>
      <div className="min-w-0 flex-1">
        {isRenaming ? (
          <ReportNameInput
            ref={inputRef}
            value={draftName}
            onValueChange={setDraftName}
            onCommit={commitRename}
            onCancel={cancelRename}
          />
        ) : (
          <button type="button" className="block w-full truncate text-left">
            <Text as="span" variant="body" className="truncate">
              {report.name || t(`${TRANSLATION_KEY}.untitled`)}
            </Text>
          </button>
        )}
        {scheduleSummary && !isRenaming && (
          <Text as="div" variant="smBodySecondary" className="truncate">
            {scheduleSummary}
          </Text>
        )}
      </div>
      {/* The menu renders in a portal, but its clicks still bubble here
          through React, so none of them reaches the row. */}
      <div
        className="flex shrink-0 items-center gap-1"
        onClick={(event) => event.stopPropagation()}
      >
        <ReportActionsMenu actions={actions} />
      </div>
    </div>
  );
};
